import React, { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Sort, Download } from "@mui/icons-material";
import ChartCard from "../../../common/components/ChartCard";
import AgeDistributionMaternityLeaveChart from "./charts/AgeDistributionMaternityLeaveChart";
import MaternityLeaveAcademicYearChart from "./charts/MaternityLeaveAcademicYearChart";
import MaternityLeaveDistrictComparisonChart from "./charts/MaternityLeaveDistrictComparisonChart";
import MaternityLeaveRequestsByPeriodTable from "./charts/MaternityLeaveRequestsByPeriodTable";
import MaternityLeaveRequestsByDistrictTable from "./charts/MaternityLeaveRequestsByDistrictTable";
import OversightSkeleton from "../../../common/components/OversightSkeleton";
import LayoutControl from "../common/LayoutControl";

const MaternityOversight = (props) => {
  const { user, loading } = props;

  const [isLoading1, setIsLoading1] = useState(true);
  const [data, setData] = useState({});
  const [layout, setLayout] = useState("default");
  const [exportLoading, setExportLoading] = useState({ image: false, pdf: false });
  const [maternityTableSortBy, setMaternityTableSortBy] = useState("period");
  const [maternityDistrictTableSortBy, setMaternityDistrictTableSortBy] =
    useState("entityName");
  const maternityTableRef = useRef(null);
  const maternityDistrictTableRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (user?.selectedEntity) {
      getMaternityOversights();
    }
  }, [user?.selectedEntity]);

  const getMaternityOversights = async () => {
    try {
      setIsLoading1(true);
      const { data } = await axios.get(`/api/oversight/maternity-leave`);

      setData(data);
      setIsLoading1(false);
    } catch (error) {
      setIsLoading1(false);
    }
  };

  const handleMaternityTableExport = () => {
    if (maternityTableRef.current && maternityTableRef.current.exportToExcel) {
      maternityTableRef.current.exportToExcel();
    }
  };

  const handleMaternityDistrictTableExport = () => {
    if (
      maternityDistrictTableRef.current &&
      maternityDistrictTableRef.current.exportToExcel
    ) {
      maternityDistrictTableRef.current.exportToExcel();
    }
  };

  const handleExportImage = async () => {
    try {
      setExportLoading(prev => ({ ...prev, image: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const link = document.createElement("a");
      link.download = `Maternity-Overview-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (error) {
      toast.error("Failed to export image. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, image: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "l" : "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
        "",
        "FAST"
      );
      pdf.save(`Maternity-Overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  const maternityTableCustomActions = useMemo(() => {
    return [
      {
        label: "Sort by",
        icon: <Sort />,
        submenu: [
          {
            label: "Period (Chronological)",
            onClick: () => setMaternityTableSortBy("period"),
            selected: maternityTableSortBy === "period",
          },
          {
            label: "Period (Latest First)",
            onClick: () => setMaternityTableSortBy("period_desc"),
            selected: maternityTableSortBy === "period_desc",
          },
        ],
      },
      {
        label: "Export to Excel",
        icon: <Download />,
        onClick: handleMaternityTableExport,
      },
    ];
  }, [maternityTableSortBy]);

  const maternityDistrictTableCustomActions = useMemo(() => {
    return [
      {
        label: "Sort by",
        icon: <Sort />,
        submenu: [
          {
            label: "District Name (A-Z)",
            onClick: () => setMaternityDistrictTableSortBy("entityName"),
            selected: maternityDistrictTableSortBy === "entityName",
          },
          {
            label: "District Name (Z-A)",
            onClick: () => setMaternityDistrictTableSortBy("entityName_desc"),
            selected: maternityDistrictTableSortBy === "entityName_desc",
          },
          {
            label: "Total Requests (Low to High)",
            onClick: () => setMaternityDistrictTableSortBy("totalRequests"),
            selected: maternityDistrictTableSortBy === "totalRequests",
          },
          {
            label: "Total Requests (High to Low)",
            onClick: () =>
              setMaternityDistrictTableSortBy("totalRequests_desc"),
            selected: maternityDistrictTableSortBy === "totalRequests_desc",
          },
        ],
      },
      {
        label: "Export to Excel",
        icon: <Download />,
        onClick: handleMaternityDistrictTableExport,
      },
    ];
  }, [maternityDistrictTableSortBy]);

  const getLayoutClass = (index) => {
    switch (layout) {
      case "leftWide":
        return index % 2 === 0 ? "col-12 col-md-8 p-0" : "col-12 col-md-4 p-0";
      case "rightWide":
        return index % 2 === 0 ? "col-12 col-md-4 p-0" : "col-12 col-md-8 p-0";
      case "full":
        return "col-12 p-0";
      default:
        return "col-12 col-md-6 p-0";
    }
  };

  if (isLoading1 || loading) {
    return <OversightSkeleton />;
  }

  return (
    <div className="pl-4 pt-2">
      <LayoutControl 
        layout={layout} 
        setLayout={setLayout} 
        onExportImage={handleExportImage}
        onExportPDF={handleExportPDF}
        exportLoading={exportLoading}
      />
      <div className="row w-100" ref={contentRef}>
        {[...Array(7)].map((_, index) => (
          <div
            key={index}
            className={
              index === 2 || index === 3 ? "col-12 p-0" : getLayoutClass(index)
            }
          >
            {index === 0 && (
              <ChartCard
                title="Age Distribution of Maternity Leave Requests Approved by HT"
                subheader="Age distribution of teachers taking maternity leave requests approved by head teacher"
              >
                <AgeDistributionMaternityLeaveChart
                  data={data?.maternityLeaveAgeDistributions}
                />
              </ChartCard>
            )}
            {index === 1 && (
              <ChartCard
                title="Academic Year Distribution of Maternity Leave Requests Approved by HT"
                subheader="Trend of maternity leave requests approved by head teacher over academic years"
              >
                <MaternityLeaveAcademicYearChart
                  data={data?.maternityLeaveAcademicYearDistributions}
                />
              </ChartCard>
            )}
            {index === 2 && (
              <ChartCard
                title="Maternity Leave Distributions by Period"
                subheader="Comprehensive overview of maternity leave requests, candidate placements, and placed candidates by period"
                customActions={maternityTableCustomActions}
                hideDefaultActions={true}
              >
                <MaternityLeaveRequestsByPeriodTable
                  data={data}
                  sortBy={maternityTableSortBy}
                  ref={maternityTableRef}
                />
              </ChartCard>
            )}
            {index === 3 && (
              <ChartCard
                title="Maternity Leave Distributions by District"
                subheader="Comprehensive overview of maternity leave requests, candidate placements, and placed candidates by district"
                customActions={maternityDistrictTableCustomActions}
                hideDefaultActions={true}
              >
                <MaternityLeaveRequestsByDistrictTable
                  data={data}
                  sortBy={maternityDistrictTableSortBy}
                  ref={maternityDistrictTableRef}
                />
              </ChartCard>
            )}
          </div>
        ))}
        <div className="col-12 p-0">
          <ChartCard
            title="District Wise Comparison of Requests Approved by HT versus Actual Placements"
            subheader="Comparison of maternity leave requests approved by head teacher and placed candidates by district"
          >
            <MaternityLeaveDistrictComparisonChart
              data={data?.maternityRequestAndOffersByDistrict}
            />
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};

export default connect(mapStateToProps, {})(MaternityOversight);
